import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { normalizeProseMirrorRange } from '../resolvedProseMirrorText';
import { normalizeHighlightSearchText } from '../highlightSearchText';
import { proseMirrorAtomText } from './proseMirrorAtomText';
import { createSearchTextIndex } from './searchTextIndex';
import type { TIndexedSearchText, TProseMirrorRange, TResolvedElementRange, TTextMatch } from './types';

type TIndexedProseMirrorText = TIndexedSearchText<TProseMirrorRange>;

/** Flattened for searching: whitespace collapsed to single spaces, and where each character came from. */
export const indexProseMirrorText = (editor: Editor, range: TProseMirrorRange): TIndexedProseMirrorText => {
  const normalizedRange = normalizeProseMirrorRange(editor, range);
  if (!normalizedRange) return { text: '', positions: [] };

  const index = createSearchTextIndex<TProseMirrorRange>();
  let previousTextParent: ProseMirrorNode | null = null;

  const addText = (value: string, at: (offset: number, nextOffset: number) => TProseMirrorRange) => {
    for (let offset = 0; offset < value.length; ) {
      const char = String.fromCodePoint(value.codePointAt(offset) ?? 0);
      const nextOffset = offset + char.length;
      index.add(char, at(offset, nextOffset));
      offset = nextOffset;
    }
  };

  editor.state.doc.nodesBetween(normalizedRange.from, normalizedRange.to, (node, pos, parent) => {
    const parentNode = parent ?? null;
    const textOwner = node.isBlock ? node : parentNode;
    const crossedBlock = previousTextParent !== null && previousTextParent !== textOwner;

    if (node.isText && node.text) {
      if (crossedBlock) index.separate({ from: pos, to: pos });
      addText(node.text, (offset, nextOffset) => ({
        from: Math.max(normalizedRange.from, pos + offset),
        to: Math.min(normalizedRange.to, pos + nextOffset),
      }));
      previousTextParent = textOwner;
      return false;
    }

    if (node.isAtom) {
      const atomText = proseMirrorAtomText(editor, node, pos);
      if (atomText) {
        if (crossedBlock) index.separate({ from: pos, to: pos });
        addText(atomText, () => ({
          from: Math.max(normalizedRange.from, pos),
          to: Math.min(normalizedRange.to, pos + node.nodeSize),
        }));
        previousTextParent = textOwner;
      }
      return false;
    }

    return true;
  });

  return index.done();
};

export const scrollRectForProseMirrorRange = (editor: Editor, from: number, to: number): DOMRect | null => {
  try {
    const start = editor.view.coordsAtPos(from);
    const end = editor.view.coordsAtPos(to);
    const left = Math.min(start.left, end.left);
    const top = Math.min(start.top, end.top);
    const right = Math.max(start.right, end.right);
    const bottom = Math.max(start.bottom, end.bottom);
    return new DOMRect(left, top, Math.max(1, right - left), Math.max(1, bottom - top));
  } catch {
    return null;
  }
};

const findRangeInIndexedProseMirrorText = (
  haystack: TIndexedProseMirrorText,
  highlightText: string
): TProseMirrorRange | null => {
  const needle = normalizeHighlightSearchText(highlightText);
  if (!needle) return null;

  const matchStart = haystack.text.indexOf(needle);
  if (matchStart === -1) return null;

  const matchEnd = matchStart + needle.length;
  const startPosition = haystack.positions[matchStart];
  const endPosition = haystack.positions[matchEnd - 1];
  if (!startPosition || !endPosition || startPosition.from >= endPosition.to) return null;

  return { from: startPosition.from, to: endPosition.to };
};

const uniqueRanges = (ranges: TProseMirrorRange[]): TProseMirrorRange[] => {
  const seen = new Set<string>();
  const result: TProseMirrorRange[] = [];

  for (const range of ranges) {
    const key = `${range.from}:${range.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(range);
  }

  return result;
};

// elementIds are ancestor-first, so search the smallest range (the cited leaf) before the broad wrapper to avoid matching an earlier occurrence.
export const buildCandidateProseMirrorRanges = (
  editor: Editor,
  resolvedRanges: TResolvedElementRange[]
): TProseMirrorRange[] => {
  if (resolvedRanges.length === 0) return [];

  const normalizedRanges = resolvedRanges
    .map((range) => normalizeProseMirrorRange(editor, range))
    .filter((range): range is TProseMirrorRange => Boolean(range));
  const bySmallestFirst = [...normalizedRanges].sort((a, b) => a.to - a.from - (b.to - b.from));
  // The widest end, not the end of the last one to start: ids arrive ancestor-first, so the range
  // that starts last is usually nested inside an earlier one and ends well before it.
  const fullSpan = normalizedRanges.length
    ? {
        from: Math.min(...normalizedRanges.map((range) => range.from)),
        to: Math.max(...normalizedRanges.map((range) => range.to)),
      }
    : undefined;

  return uniqueRanges([...bySmallestFirst, ...(fullSpan ? [fullSpan] : [])]);
};

export const findProseMirrorQuoteMatch = (
  editor: Editor,
  candidateRanges: TProseMirrorRange[],
  highlightTexts: string[]
): TTextMatch | null => {
  for (const range of candidateRanges) {
    for (const needle of highlightTexts) {
      const match = findRangeInIndexedProseMirrorText(indexProseMirrorText(editor, range), needle);
      if (!match) continue;
      const scrollRect = scrollRectForProseMirrorRange(editor, match.from, match.to);
      if (scrollRect) return { ...match, scrollRect };
    }
  }

  return null;
};
